import React, { useMemo, useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import moment from "moment"

import { EvaluationSidebar } from "./Components/EvaluationSidebar"
import EvaluationCard from "./Components/evaluationCard"
import ExtraEventCard from "./Components/ExtraEventCard"
import { useEvaluationData } from "./Hooks/useEvaluationData"
import { toISODate } from "../../utils/date"
import { NeoButton, Breadcrumb, MasterDetailLayout } from "../../components/Common"
import NeoLoader from "../../components/Common/NeoLoader"

const occursOnDate = (schedule, isoDate) => {
  const sessionDate = schedule?.sessionDate || null
  if (sessionDate) {
    // Compara diretamente a string "YYYY-MM-DD" para evitar bugs de timezone no JS
    return String(sessionDate).slice(0, 10) === String(isoDate).slice(0, 10)
  }
  return false
}

/**
 * Página de Avaliação de Metodologia.
 * Usa useEvaluationData para buscar sessões e dados (sem GradeContext).
 */
const EvaluationPage = () => {
  const location = useLocation()

  // 1. Data de referência local (sem depender de GradeContext)
  const [referenceDate, setReferenceDate] = useState(new Date())

  // 2. Dados da avaliação (sessões, employees, atividades)
  const { sessions, employees, loading } = useEvaluationData({ referenceDate })

  // 3. Estados Locais da Página de Avaliação
  const [selectedSchedule, setSelectedSchedule] = useState(null)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("")
  const [activeTab, setActiveTab] = useState(
    location.pathname.includes("tests") ? "performance" : "technical"
  )

  // Sync tab with URL changes
  useEffect(() => {
    setActiveTab(location.pathname.includes("tests") ? "performance" : "technical")
  }, [location.pathname])

  useEffect(() => {
    const title = location.pathname.includes("tests") ? "Testes de Performance" : "Avaliação Técnica"
    document.title = `${title} | NeoApp`
  }, [location.pathname])

  // Filtro de sessões do dia (sem filtro de professor) para extrair os professores ativos
  const todayAllSchedules = useMemo(() => {
    const todayISO = toISODate(referenceDate)
    return (sessions || []).filter(schedule => schedule && occursOnDate(schedule, todayISO))
  }, [sessions, referenceDate])

  // Filtro final de sessões a exibir, aplicando o filtro de professor
  const todaySchedules = useMemo(() => {
    return todayAllSchedules
      .filter(schedule => !selectedEmployeeId || String(schedule.idEmployee) === String(selectedEmployeeId))
      .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00"))
  }, [todayAllSchedules, selectedEmployeeId])

  // Scroll to top when schedule is selected
  useEffect(() => {
    if (selectedSchedule) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedSchedule]);

  const activeEmployees = useMemo(() => {
    const activeEmployeeIds = new Set(todayAllSchedules.map(s => String(s.idEmployee)))
    return [...(employees || [])]
      .filter(emp => activeEmployeeIds.has(String(emp.id)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
  }, [employees, todayAllSchedules])

  const handlePrevDay = () => {
    setReferenceDate(prev => moment(prev).subtract(1, 'days').toDate())
    setSelectedSchedule(null)
  }

  const handleNextDay = () => {
    setReferenceDate(prev => moment(prev).add(1, 'days').toDate())
    setSelectedSchedule(null)
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <NeoLoader type="inline" loading={loading} />
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-3 px-2 px-md-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Breadcrumb title="Avaliação de Metodologia" />
      </div>

      <div className="border-top mb-3 d-none d-md-block"></div>

      <div className="evaluation-container">
        <MasterDetailLayout
          master={
            <div className={selectedSchedule ? "d-none d-md-block" : ""}>
              <EvaluationSidebar
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedEmployeeId={selectedEmployeeId}
                setSelectedEmployeeId={setSelectedEmployeeId}
                employees={activeEmployees}
                currentDate={referenceDate}
                handlePrevDay={handlePrevDay}
                handleNextDay={handleNextDay}
                todaySchedules={todaySchedules}
                selectedSchedule={selectedSchedule}
                setSelectedSchedule={setSelectedSchedule}
              />
            </div>
          }
          detail={
            <div className={!selectedSchedule ? "d-none d-md-block" : ""}>
              <div className="d-md-none mb-3">
                <NeoButton variant="light" className="w-100 shadow-sm border-0 fw-bold" onClick={() => setSelectedSchedule(null)} icon="mdi-arrow-left">
                  Voltar para Lista de Turmas
                </NeoButton>
              </div>

              {activeTab === "extras" ? (
                <ExtraEventCard schedule={selectedSchedule} />
              ) : (
                <EvaluationCard
                  schedule={selectedSchedule}
                  activeMode={activeTab}
                />
              )}
            </div>
          }
        />
      </div>
    </React.Fragment>
  )
}

export default EvaluationPage